import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MessagingAccessButton } from '../features/direct-messages/condensed/MessagingAccessButton';
import { useCapability } from '../hooks/use-capability';
import { useMyTeamPresence, useUpdateOwnPresence } from '../hooks/use-presence';
import { PRESENCE_LABELS } from '../lib/presence-labels';

import { AppearanceMenuSection } from './AppearanceMenuSection';
import { NotificationsTray } from './NotificationsTray';
import { UserAvatar } from './UserAvatar';

import type { PresenceWireItem } from '../lib/api-client';
import type { AuthTenant, AuthUser } from '../lib/types';

interface TopbarProps {
  tenant: AuthTenant | null;
  user: AuthUser | null;
  onLogout: () => void;
  /**
   * Abre o Localizador Universal (⌘K). Substituiu `onOpenShortcuts`, que o
   * botão de busca chamava desde o Sprint Visual Brief 2A r7b — e que NENHUM
   * caller de `src/` passava: o botão era inerte, clicar não fazia nada.
   */
  onOpenSearch?: () => void;
}

const TIER_LABELS: Record<string, string> = {
  solo: 'Solo',
  team: 'Team',
  professional: 'Professional',
  regulated: 'Regulated',
};

const ROLE_LABELS: Record<string, string> = {
  atendente: 'Atendente',
  supervisor: 'Supervisor',
  gestor: 'Gestor',
  admin_tenant: 'Admin Tenant',
  dpo_tenant: 'DPO',
  suporte_lume: 'Suporte Lume',
  engenharia_lume: 'Engenharia Lume',
  direcao_lume: 'Direção Lume',
};

function tenantInitials(name: string): string {
  // "NetComet" → "NC"; "Fisio Roberta" → "FR"
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    const first = parts[0] ?? '';
    const slice = first.slice(0, 2);
    return (slice.length > 0 ? slice : '?').toUpperCase();
  }
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts[parts.length - 1]?.charAt(0) ?? '';
  const combined = `${first}${last}`.toUpperCase();
  return combined.length > 0 ? combined : '?';
}

/**
 * Topbar canonical — Sprint Visual Brief 2A r7b (Agent A SHELL).
 *
 * Layout 52px:
 *   [Lume glyph + wordmark] [Tenant pill: NC NetComet · Professional]
 *   [spacer] [Buscar ⌘K] [bell] [cog] [User identity pill]
 *
 * «Buscar ⌘K» abre o Localizador Universal desde 14/Ago/2026 — antes disso o
 * botão existia e era INERTE. Sino e engrenagem deixaram de ser placeholders em
 * 04/Set/2026 (A-04/A-35): sino = NotificationsTray; engrenagem = hub Admin (gateada).
 */
export function Topbar({ tenant, user, onLogout, onOpenSearch }: TopbarProps): JSX.Element {
  const tenantName = tenant?.name ?? '—';
  const tenantTier = TIER_LABELS[tenant?.packageType ?? ''] ?? tenant?.packageType ?? '—';
  const userName = user?.name ?? '—';
  const userRole = ROLE_LABELS[user?.primaryRole ?? ''] ?? user?.primaryRole ?? '';
  const navigate = useNavigate();
  const canOpenSettings = useCapability('tenant-config-read');

  return (
    <header className="topbar" role="banner" aria-label="Lume Atendimento Topbar">
      <span className="topbar__brand">
        <span aria-hidden className="topbar__brand-glyph" />
        <span>Lume Atendimento</span>
      </span>

      <span className="topbar__tenant" title={`${tenantName} · ${tenantTier}`}>
        <span className="topbar__tenant-logo" aria-hidden>
          {tenantInitials(tenantName)}
        </span>
        <span>{tenantName}</span>
        <span aria-hidden className="topbar__tenant-tier">
          · {tenantTier}
        </span>
      </span>

      <span className="topbar__spacer" />

      <button
        type="button"
        className="topbar__search"
        aria-label="Buscar (Cmd K)"
        onClick={onOpenSearch}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-3.5-3.5" strokeLinecap="round" />
        </svg>
        <span className="topbar__search-label">Buscar</span>
        <kbd className="topbar__search-kbd">⌘K</kbd>
      </button>

      {/* Mensageria interna condensada — à ESQUERDA do sino (pessoas ≠ sistema).
          Self-gated em P19 (direct-message-participate): Solo não renderiza nada. */}
      <MessagingAccessButton />

      {/* Sino REAL (A-35, noturna 04/Set/2026): handoffs pendentes dirigidos a mim +
          modal Aceitar/Recusar. Existia desde a Fatia G sem estar montado. */}
      <NotificationsTray />

      {/* Engrenagem: só para quem pode ler a config do tenant; leva ao hub Admin.
          Antes era um botão inerte com título «(placeholder)» (A-04). */}
      {canOpenSettings && (
        <button
          type="button"
          className="topbar__icon-btn"
          aria-label="Configurações do tenant"
          title="Configurações do tenant"
          onClick={() => navigate('/admin')}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
      )}

      <UserMenu user={user} userName={userName} userRole={userRole} onLogout={onLogout} />
    </header>
  );
}

type PresenceStatus = PresenceWireItem['status'];

/** Rótulos em linguagem de operação — nunca o enum cru. Domicílio único: `lib/presence-labels`. */
const PRESENCE_OPTIONS: readonly { status: PresenceStatus; label: string; hint: string }[] = [
  { status: 'online', label: PRESENCE_LABELS.online, hint: 'Recebe conversas da fila' },
  { status: 'away', label: PRESENCE_LABELS.away, hint: 'Pausa curta — não recebe novas' },
  { status: 'busy', label: PRESENCE_LABELS.busy, hint: 'Em atendimento sem interrupção' },
  { status: 'offline', label: PRESENCE_LABELS.offline, hint: 'Fora do turno' },
];

/**
 * Menu do usuário: status de presença + Sair.
 *
 * A-31 (noturna 04/Set/2026): `useUpdateOwnPresence` tinha 0 consumidores — ninguém
 * na UI chamava `PATCH /v1/presence/me`, logo nenhum operador jamais ficava
 * «online»: o Painel da equipe mostrava «0/6 · 6 offline» com 5 sessões abertas e
 * a IA dizia ao cliente «não há ninguém disponível». Este menu é o gesto de
 * entrar/sair de turno. Presença é conceito universal de operação — vale para
 * clínica, escritório e loja igual.
 */
function UserMenu({
  user,
  userName,
  userRole,
  onLogout,
}: {
  user: AuthUser | null;
  userName: string;
  userRole: string;
  onLogout: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const presence = useUpdateOwnPresence();
  const team = useMyTeamPresence();
  const mine = team.data.find((p) => p.userId === user?.id);
  const [status, setStatus] = useState<PresenceStatus>(mine?.status ?? 'offline');

  useEffect(() => {
    if (mine?.status !== undefined) setStatus(mine.status);
  }, [mine?.status]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = PRESENCE_OPTIONS.find((o) => o.status === status);

  return (
    <div className="topbar__user" ref={rootRef}>
      <button
        type="button"
        className="topbar__op"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${userName} · ${current?.label ?? status} · abrir menu`}
        title={`${userName} · ${current?.label ?? status}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="topbar__avatar-wrap">
          <UserAvatar name={userName} colorKey={user?.id ?? userName} size="sm" />
          <span className={`topbar__status-dot topbar__status-dot--${status}`} aria-hidden />
        </span>
        <span className="topbar__op-meta">
          <b>{userName.split(' ')[0] ?? userName}</b>
          <span>{userRole}</span>
        </span>
      </button>

      {open && (
        <div className="topbar__menu" role="menu" aria-label="Menu do usuário">
          <div className="topbar__menu-title">Sua presença</div>
          {PRESENCE_OPTIONS.map((o) => (
            <button
              key={o.status}
              type="button"
              role="menuitemradio"
              aria-checked={o.status === status}
              className="topbar__menu-item"
              disabled={presence.isPending}
              onClick={() => {
                presence.mutate({ status: o.status }, { onSuccess: () => setStatus(o.status) });
                setOpen(false);
              }}
            >
              <span className={`topbar__status-dot topbar__status-dot--${o.status}`} aria-hidden />
              <span className="topbar__menu-item-label">
                <b>{o.label}</b>
                <span>{o.hint}</span>
              </span>
            </button>
          ))}
          <div className="topbar__menu-sep" role="separator" />

          {/* Aparência (tema claro/escuro/sistema + densidade) — Eixo 3 do
              Briefing 1 V2. Os tokens `[data-theme="dark"]` existiam no Design
              System desde a origem e nenhum operador conseguia alcançá-los:
              `index.html` chumbava `data-theme="light"` e nada no código dos
              três apps escrevia o atributo. Este é o interruptor que faltava. */}
          <AppearanceMenuSection />

          <div className="topbar__menu-sep" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="topbar__menu-item topbar__menu-item--danger"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            Sair
          </button>
        </div>
      )}
    </div>
  );
}
